import React, { useEffect, useState } from "react";
import { DoorOpen } from "lucide-react";
import { Button } from "../../../components/new-components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../../components/new-components/ui/dialog";
import { CloseoutPreview, CloseoutResult, closeoutWords, heldWords, visitNotesApi } from "./visitNotesApi";

/**
 * "Close out this visit" (facility records Phase 0c; Isaiah Cotto, 2026-10-27: "send it with confirmation when closing
 * out a facility for the day so it's sent on their way out"). The check-out that pairs with Confirm arrival.
 *
 * The dialog says who the records go to (the building's records recipient, verified or not), how many residents and
 * files, and what happens when sending cannot happen: "Saved and held for the VA; nothing was sent". A resident seen
 * today with no visit note blocks it and is listed with Write note (names only of your own residents). The server
 * decides everything again; closing out never fails because delivery cannot happen.
 */
/**
 * What became of today's first send, beside the button (re-check #6 item 4): a provider told "the records go at h:mm"
 * sees if it was stopped or cancelled instead. Times only; the reason is the office's.
 */
const deliveryLine = (d: CloseoutPreview["delivery"]): string | null => {
  if (!d) return null;
  if (d.state === "WAITING" && d.send_at) return `The records go to the building at ${new Date(d.send_at).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" })} Eastern (the office can cancel it).`;
  if (d.state === "STOPPED") return "The first send was stopped; the office is checking. Nothing was sent.";
  if (d.state === "CANCELLED") return "The office cancelled the first send. Nothing was sent.";
  return null;
};

export interface CloseOutVisitProps {
  facilityId: string;
  seenCount: number;
  onOpenResident?: (patientId: string) => void;
  onClosedOut?: (r: CloseoutResult) => void;
}

const ROLE = (r: string | null) => (r ? r.replace(/_/g, " ").toLowerCase() : "records contact");

export function CloseOutVisit({ facilityId, seenCount, onOpenResident, onClosedOut }: CloseOutVisitProps) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<CloseoutPreview | null>(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<CloseoutResult | null>(null);
  // Offered only when the server serves it: asked once per building, so the button never shows while the
  // FACILITY_RECORDS_VISIT_NOTES switch is off, before the migration, or for someone who may not close out.
  const [served, setServed] = useState(false);
  const [delivery, setDelivery] = useState<CloseoutPreview["delivery"]>(null);
  useEffect(() => {
    let live = true;
    setServed(false);
    visitNotesApi.preview(facilityId)
      .then((r) => {
        if (!live) return;
        const ok = Boolean(r.success && r.data && Array.isArray(r.data.missing) && r.data.counts);
        setServed(ok);
        // What became of today's first send, shown beside the button (no PHI: a state and a time).
        setDelivery(ok && r.data ? r.data.delivery || null : null);
      })
      .catch(() => { if (live) setServed(false); });
    return () => { live = false; };
  }, [facilityId]);

  const start = async () => {
    setError(""); setResult(null); setPreview(null); setOpen(true);
    try {
      const r = await visitNotesApi.preview(facilityId);
      if (r.success && r.data && Array.isArray(r.data.missing) && r.data.counts) setPreview(r.data);
      else if (r.status === 503 || r.status === 403 || r.success) { setHidden(true); setOpen(false); }
      else setError(r.message || r.error || "The close-out could not be prepared.");
    } catch { setError("Could not reach eMerge."); }
  };
  const confirm = async () => {
    setBusy(true); setError("");
    try {
      const r = await visitNotesApi.closeOut(facilityId);
      if (r.success && r.data) { setResult(r.data); if (onClosedOut) onClosedOut(r.data); } else if (r.code === "NOTES_MISSING") {
        const p = await visitNotesApi.preview(facilityId);
        if (p.success && p.data) setPreview(p.data);
        setError(r.message || "A resident seen today has no visit note.");
      } else setError(r.message || r.error || "The visit was not closed out.");
    } catch { setError("eMerge did not confirm. Check again before closing out a second time."); } finally { setBusy(false); }
  };
  if (hidden || !served || seenCount < 1) return null;

  const p = preview;
  const missing = p ? p.missing : [];
  return (
    <>
      <Button type="button" variant="outline" onClick={start} data-testid="close-out-visit"><DoorOpen aria-hidden="true" /> Close out this visit</Button>
      {deliveryLine(delivery) && <p className="fw-closeout-delivery" role="status" data-testid="close-out-delivery">{deliveryLine(delivery)}</p>}
      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Close out this visit</DialogTitle>
            <DialogDescription>{p ? `${p.building_name || "This building"}, today.` : "Reading today's visit…"}</DialogDescription>
          </DialogHeader>
          {p && !result && (
            <div className="fw-closeout">
              {p.closed && <p role="alert" className="fw-error">{p.closed}: the visit cannot be closed out here.</p>}
              {missing.length > 0 ? (
                <>
                  <p>{missing.length} {missing.length === 1 ? "resident seen today has" : "residents seen today have"} no visit note. Write one, or mark &ldquo;No note needed&rdquo; with the reason.</p>
                  <ul aria-label="Residents with no visit note">
                    {missing.map((m) => (
                      <li key={m.patient_id}>
                        {m.yours ? m.name : "A resident another provider saw"}
                        {m.yours && onOpenResident && (
                          <Button type="button" size="sm" variant="outline" onClick={() => { setOpen(false); onOpenResident(m.patient_id); }}>Write note</Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <p>
                    {p.counts.seen} {p.counts.seen === 1 ? "resident" : "residents"} seen ({p.counts.noted} with a note{p.counts.no_note ? `, ${p.counts.no_note} with no note needed` : ""}); {p.counts.files} {p.counts.files === 1 ? "file" : "files"} ready.
                  </p>
                  <p>
                    Records go to the building&rsquo;s {ROLE(p.recipient.role)}{p.recipient.name ? `, ${p.recipient.name}` : ""}
                    {p.recipient.channel === "EMAIL" ? (p.recipient.verified ? " (verified)." : " (not verified yet).") : "."}
                  </p>
                  {!p.will_send && <p className="fw-muted">Sending cannot happen now ({p.send_on ? "the recipient is not ready" : "sending is switched off"}): it will be saved and held for the VA; nothing will be sent.</p>}
                  {p.closeouts.length > 0 && <p className="fw-muted">Already closed out {p.closeouts.length} {p.closeouts.length === 1 ? "time" : "times"} today: closing out again sends only what is new.</p>}
                </>
              )}
            </div>
          )}
          {result && (
            <div role="status" className="fw-closeout-done">
              <p><strong>Visit closed out.</strong></p>
              <p>{closeoutWords(result.send)} {result.send.outcome === "SENT" || result.send.outcome === "QUEUED" || result.send.reason === "ALREADY_SENT" || result.send.reason === "SEND_QUEUED" || !result.send.reason ? "" : heldWords(result.send.reason)}</p>
            </div>
          )}
          {error && <p role="alert" className="fw-error">{error}</p>}
          <DialogFooter>
            {!result && p && missing.length === 0 && !p.closed && (
              <Button type="button" onClick={confirm} disabled={busy}>{busy ? "Closing out…" : p.confirm_words}</Button>
            )}
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>{result ? "Done" : "Cancel"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
export default CloseOutVisit;
